import { useMemo, useState } from "react";
import { useNotify, DateField, FunctionField, useDataProvider } from "react-admin";
import { Alert, Box, Button, FormControlLabel, Switch, Typography } from "@mui/material";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import BalanceOutlined from "@mui/icons-material/BalanceOutlined";
import CalculateOutlined from "@mui/icons-material/CalculateOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { SectionCard } from "@/components/common/SectionCard";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import type { AggregateBreachCalculation, AggregateBreachClaimantDetail } from "@/types";
import { useAggregateBreachCalculations } from "./useAggregateBreachCalculations";
import { BreachClaimantDetail } from "./BreachClaimantDetail";
import { downloadBreachCalculationsCSV, downloadBreachReport } from "./export/exportAggregateData";

interface BreachSectionProps {
  policyId: string;
}

export const BreachSection = ({ policyId }: BreachSectionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const dataProvider = useDataProvider();
  const { calculations, isLoading, isError, triggerCalculation, isCalculating } =
    useAggregateBreachCalculations(policyId);
  const sortedCalculations = useMemo(
    () =>
      [...calculations].sort((a, b) => new Date(b.created_datetime).getTime() - new Date(a.created_datetime).getTime()),
    [calculations]
  );
  // By default hide superseded rows (re-runs leave them queryable for audit
  // but they shouldn't clutter the day-to-day view). Toggle reveals them.
  // Mirrors AccommodationTable.tsx:54-58.
  const visibleCalculations = useMemo(
    () => (showSuperseded ? sortedCalculations : sortedCalculations.filter((c) => !c.is_superseded)),
    [sortedCalculations, showSuperseded]
  );
  const supersededCount = useMemo(() => sortedCalculations.filter((c) => c.is_superseded).length, [sortedCalculations]);

  // TODO(perm): replace with a dedicated 'calculate' permission once role gating is defined
  const { canEdit } = useResourcePermissions("policies");

  const handleConfirm = () => {
    setDialogOpen(false);
    triggerCalculation(undefined, {
      onSuccess: () => notify("Breach calculation completed successfully", { type: "success" }),
      onError: (error) => notifyError(error),
    });
  };

  // Exports use visibleCalculations (matches what the user sees in the table).
  // Mirrors AccommodationTable.tsx:122.
  const handleExportCSV = () => downloadBreachCalculationsCSV(visibleCalculations, policyId);

  const handleExportReport = async () => {
    const calc = visibleCalculations[0];
    if (!calc || isExporting) return;
    setIsExporting(true);
    try {
      // Use dataProvider.getList for click-time fetches (codebase convention,
      // 15+ sites). The /details endpoint is not server-paginated, but the
      // data provider still unwraps the {data, pagination} envelope.
      const { data: details } = await dataProvider.getList<AggregateBreachClaimantDetail>(
        `policies/${policyId}/aggregate-calculations/${calc.id}/details`,
        {
          pagination: { page: 1, perPage: 10000 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        }
      );
      await downloadBreachReport(calc, details, policyId);
    } catch (error) {
      notifyError(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <SectionCard
        title="Aggregate Claim"
        collapsible
        isExpanded={expanded}
        onToggle={() => setExpanded((e) => !e)}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            {canEdit && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalculateOutlined />}
                onClick={() => setDialogOpen(true)}
                disabled={isCalculating}
              >
                Calculate Breach
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlined />}
              onClick={handleExportCSV}
              disabled={visibleCalculations.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlined />}
              onClick={handleExportReport}
              disabled={visibleCalculations.length === 0 || isExporting}
            >
              {isExporting ? "Exporting…" : "Export Report"}
            </Button>
          </Box>
        }
      >
        {isError ? (
          <Alert severity="error">Failed to load breach calculations. Please refresh and try again.</Alert>
        ) : (
          <>
            {supersededCount > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showSuperseded}
                      onChange={(e) => setShowSuperseded(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Show superseded calculations ({supersededCount})
                    </Typography>
                  }
                />
              </Box>
            )}
            <PaginatedDatagrid
              data={visibleCalculations}
              isLoading={isLoading}
              sort={{ field: "created_datetime", order: "DESC" }}
              resource="policies"
              // Rows are AggregateBreachCalculation records with an expand panel;
              // clicking a row toggles the inline BreachClaimantDetail. Without
              // this, react-admin's Datagrid defaults to rowClick="edit" and
              // navigates to /policies/{breachCalcId} → 404 (#5438). Mirrors the
              // AccommodationTable fix (#4635).
              rowClick="expand"
              expand={<BreachClaimantDetail />}
              empty={
                <ListEmptyState
                  icon={BalanceOutlined}
                  message="No breach calculations yet"
                  subtitle="Calculations appear here once the aggregate deductible is evaluated."
                />
              }
            >
              <DateField source="created_datetime" label="Date" showTime />
              <FunctionField
                source="is_breached"
                label="Breached"
                render={(record: AggregateBreachCalculation) => (
                  <StatusChip
                    label={record.is_breached ? "Yes" : "No"}
                    color={record.is_breached ? "error" : "success"}
                    size="small"
                  />
                )}
              />
              <FunctionField
                source="breach_amount"
                label="Breach Amount"
                render={(record: AggregateBreachCalculation) => formatUSD(record.breach_amount)}
              />
              <FunctionField
                source="carrier_liability"
                label="Carrier Liability"
                render={(record: AggregateBreachCalculation) => formatUSD(record.carrier_liability)}
              />
              <FunctionField
                source="net_carrier_liability"
                label="Net Carrier Liability"
                render={(record: AggregateBreachCalculation) => formatUSD(record.net_carrier_liability)}
              />
              <FunctionField
                source="is_superseded"
                label="Status"
                render={(record: AggregateBreachCalculation) =>
                  record.is_superseded ? (
                    <StatusChip label="Superseded" size="small" color="muted" />
                  ) : (
                    <StatusChip label="Active" size="small" color="success" />
                  )
                }
              />
            </PaginatedDatagrid>
          </>
        )}
      </SectionCard>

      <ConfirmationDialog
        open={dialogOpen}
        title="Calculate Breach"
        message="This will calculate the aggregate breach position for this policy period. Continue?"
        onConfirm={handleConfirm}
        onCancel={() => setDialogOpen(false)}
      />
    </>
  );
};
